import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Wind, Eye, Heart, Brain, PenLine, Sparkles, Sun, Lock, Flower2, Timer, Lightbulb, Shield } from "lucide-react";
import { PageSEO, FAQSchema } from "@/components/SEO";
import BreathingTool from "@/components/toolkit/BreathingTool";
import GroundingTool from "@/components/toolkit/GroundingTool";
import MoodCheckin from "@/components/toolkit/MoodCheckin";
import ThoughtReframe from "@/components/toolkit/ThoughtReframe";
import GratitudeJournal from "@/components/toolkit/GratitudeJournal";
import AffirmationBuilder from "@/components/toolkit/AffirmationBuilder";

const TOOLKIT_FAQS = [
  { q: "Are these tools free?", a: "Yes — all tools on this page are completely free. They're our way of making wellness accessible to everyone. Our paid kits go deeper with structured programmes and additional resources." },
  { q: "Do these tools replace therapy?", a: "No. These are self-help exercises for learning and practice. They don't diagnose or replace professional care. If you're in crisis, please contact local emergency services." },
  { q: "Is my data saved anywhere?", a: "Your entries stay in your browser only. Nothing is sent to a server. Your reflections are private." },
  { q: "Where should I start?", a: "If you're feeling anxious, start with the Breathing Anchor. For overwhelm, try 5-4-3-2-1 Grounding. For negative thinking, use the Thought Reframe tool." },
];

const TOOLS = [
  { id: "breathing", name: "Breathing Anchor", desc: "A guided 4-7-8 breathing exercise to calm anxiety and centre your mind.", icon: Wind, category: "Calm & Focus", free: true, tags: ["calm", "breathing", "anxiety"] },
  { id: "grounding", name: "Sensory Grounding", desc: "An interactive 5-4-3-2-1 exercise to ground yourself in the present moment.", icon: Eye, category: "Calm & Focus", free: true, tags: ["grounding", "mindfulness", "presence"] },
  { id: "mood", name: "Mood Check-In", desc: "A quick emotional check-in to build self-awareness and track how you feel.", icon: Heart, category: "Self-Awareness", free: true, tags: ["emotions", "awareness", "check-in"] },
  { id: "reframe", name: "Thought Reframe", desc: "Identify, challenge, and reframe negative thoughts using CBT techniques.", icon: Brain, category: "Thoughts & Emotions", free: true, tags: ["CBT", "thoughts", "reframe"] },
  { id: "gratitude", name: "Gratitude Journal", desc: "A guided gratitude practice to shift your focus to what's going well.", icon: Flower2, category: "Positivity & Growth", free: true, tags: ["gratitude", "journal", "positivity"] },
  { id: "affirmation", name: "Affirmation Builder", desc: "Generate personalised positive affirmations to build confidence and resilience.", icon: Sun, category: "Confidence", free: true, tags: ["affirmations", "self-talk", "confidence"] },
];

const CATEGORIES = [...new Set(TOOLS.map((t) => t.category))];

const ToolCard = ({ tool, isActive, onClick }) => {
  const Icon = tool.icon;
  return (
    <button data-testid={`tool-card-${tool.id}`} onClick={onClick}
      className="w-full text-left p-5 rounded-2xl transition-all duration-300 hover:-translate-y-0.5"
      style={{
        background: isActive ? "white" : "transparent",
        border: `1px solid ${isActive ? "var(--ll-green)" : "var(--ll-border)"}`,
        boxShadow: isActive ? "0 4px 16px rgba(42,54,35,0.08)" : "none",
      }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: isActive ? "var(--ll-green)" : "var(--ll-bg-secondary)" }}>
          <Icon size={18} style={{ color: isActive ? "white" : "var(--ll-text-secondary)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium truncate" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{tool.name}</p>
            {tool.free && <span className="text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Free</span>}
            {!tool.free && <Lock size={10} style={{ color: "var(--ll-text-muted)" }} />}
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{tool.desc}</p>
        </div>
      </div>
    </button>
  );
};

const TOOL_COMPONENTS = {
  breathing: BreathingTool,
  grounding: GroundingTool,
  mood: MoodCheckin,
  reframe: ThoughtReframe,
  gratitude: GratitudeJournal,
  affirmation: AffirmationBuilder,
};

const ToolkitPage = () => {
  const [activeTool, setActiveTool] = useState("breathing");
  const ActiveComponent = TOOL_COMPONENTS[activeTool];
  // CATEGORIES and TOOLS are module-level constants — stable across renders
  const toolsByCategory = useMemo(() =>
    CATEGORIES.map((cat) => ({ cat, tools: TOOLS.filter((t) => t.category === cat) })),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <main data-testid="toolkit-page" className="pt-20">
      <PageSEO title="Free Interactive Wellness Toolkit" description="Free, evidence-informed interactive mental wellness tools: breathing exercises, grounding techniques, thought reframing, gratitude journal, and more." path="/toolkit" />
      <FAQSchema faqs={TOOLKIT_FAQS} />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6" style={{ background: "var(--ll-accent)", color: "var(--ll-text)" }}>
              <Sparkles size={14} />
              <span className="text-xs font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>Free Interactive Tools</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] mb-6" style={{ color: "var(--ll-text)" }}>
              Your Wellness{" "}
              <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>Toolkit</em>
            </h1>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Evidence-informed interactive exercises for calm, clarity, and emotional resilience.
              Use them anytime — your data stays private in your browser.
            </p>
          </div>
        </div>
      </section>

      {/* Toolkit */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tool Sidebar */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-28 space-y-6">
                {toolsByCategory.map(({ cat, tools }) => (
                  <div key={cat}>
                    <p className="text-[10px] tracking-[0.2em] uppercase mb-2 px-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{cat}</p>
                    <div className="space-y-2">
                      {tools.map((tool) => (
                        <ToolCard key={tool.id} tool={tool} isActive={activeTool === tool.id} onClick={() => setActiveTool(tool.id)} />
                      ))}
                    </div>
                  </div>
                ))}

                {/* Kit Upsell */}
                <div className="p-5 rounded-2xl" style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.1), rgba(163,184,153,0.12))", border: "1px solid var(--ll-border)" }}>
                  <p className="text-xs font-medium mb-1" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Want structured programmes?</p>
                  <p className="text-[11px] leading-relaxed mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Our wellness kits go deeper with guided plans, worksheets, and expert-curated content.</p>
                  <Link to="/#products" className="text-xs font-medium flex items-center gap-1" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                    Explore Kits — from R250 <Sparkles size={10} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Active Tool */}
            <div className="lg:col-span-2">
              {ActiveComponent && <ActiveComponent />}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ToolkitPage;
